import { useEffect, useState } from 'react'
import { Modal, Empty, Descriptions, Message, Spin, Tag } from '@arco-design/web-react'
import { get } from '@/api/client'
import type { Channel } from '@/types'

interface Props {
  visible: boolean
  channel: Channel | null
  onCancel: () => void
}

interface CodexUsage {
  total_requests?: number
  success_requests?: number
  fail_requests?: number
  last_used_time?: number
  quota?: number
  models?: Record<string, number>
  [k: string]: unknown
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

export default function CodexUsageModal({ visible, channel, onCancel }: Props) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<unknown>(null)

  useEffect(() => {
    if (!visible || !channel) return
    const load = async () => {
      setLoading(true)
      setData(null)
      try {
        const res = await get<unknown>(`/api/channel/${channel.id}/codex/usage`)
        setData(res)
      } catch (err: unknown) {
        Message.warning(err instanceof Error ? err.message : '获取 Codex 统计失败')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [visible, channel])

  const structured = isRecord(data) ? (data as CodexUsage) : null
  const hasStructuredFields =
    structured &&
    (structured.total_requests !== undefined ||
      structured.quota !== undefined ||
      structured.models !== undefined)

  return (
    <Modal
      title={channel ? `Codex 使用统计 · ${channel.name}` : 'Codex 使用统计'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      style={{ width: 640 }}
    >
      {loading ? (
        <div className="py-10 flex justify-center">
          <Spin />
        </div>
      ) : data && hasStructuredFields && structured ? (
        <Descriptions
          column={2}
          data={[
            { label: '总请求', value: structured.total_requests ?? '-' },
            { label: '成功请求', value: structured.success_requests ?? '-' },
            { label: '失败请求', value: structured.fail_requests ?? '-' },
            { label: '消耗额度', value: structured.quota ?? '-' },
            {
              label: '最近使用',
              value: structured.last_used_time
                ? new Date(structured.last_used_time * 1000).toLocaleString('zh-CN')
                : '-',
            },
            {
              label: '按模型分布',
              value: structured.models ? (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(structured.models).map(([m, c]) => (
                    <Tag key={m} color="arcoblue">
                      {m}: {c}
                    </Tag>
                  ))}
                </div>
              ) : (
                '-'
              ),
            },
          ]}
        />
      ) : data !== null ? (
        <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap break-all">
          {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
        </pre>
      ) : (
        <Empty description="暂无数据" />
      )}
    </Modal>
  )
}
